/**
 * 商品 Drawer 內容（預購／線上商店／線上點餐共用）
 * 含：圖片（支援多圖輪播）、描述、多規格選擇器、標題與價格、數量 Stepper、加入購物車按鈕
 */

import { DrawerTitle, DrawerFooter } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { formatTwdFromCents } from '@pos/shared/money';
import { ProductImageGallery } from './ProductImageGallery';

/** 規格維度標籤對照 */
const DIM_LABEL: Record<string, string> = {
  size: '尺寸',
  color: '顏色',
};

export type ProductDrawerVariant = {
  id: string;
  unitPriceTwd: number;
  combination?: Record<string, string>;
};

export type ProductDrawerProduct = {
  productName: string;
  productDescription?: string | null;
  /** 主圖 URL（由父層傳入，可為已正規化） */
  imageUrl: string | null;
  /** 多張圖片 URL（輪播用；主圖可不在其中） */
  imageUrls?: string[];
  productPriceTwd: number;
  hasVariants?: boolean;
  variants?: ProductDrawerVariant[];
};

export type ProductDrawerContentProps = {
  product: ProductDrawerProduct;
  selectedByDimension: Record<string, string> | null;
  setSelectedByDimension: (
    prev:
      | Record<string, string>
      | null
      | ((p: Record<string, string> | null) => Record<string, string> | null)
  ) => void;
  /** 由 selectedByDimension 對應到的規格（父層計算後傳入） */
  selectedVariant: ProductDrawerVariant | null;
  quantity: number;
  onQuantityMinus: () => void;
  onQuantityPlus: () => void;
  onAddToCart: () => void;
  maxQuantity: number;
  stepperAtMin: boolean;
  stepperAtMax: boolean;
  addToCartLabel?: string;
};

/**
 * 商品 Drawer 內文：圖片、描述、規格選擇、標題與價格、Stepper、加入購物車
 * 與 PreorderPage / OnlineShopPage 共用
 */
export function ProductDrawerContent({
  product,
  selectedByDimension,
  setSelectedByDimension,
  selectedVariant,
  quantity,
  onQuantityMinus,
  onQuantityPlus,
  onAddToCart,
  stepperAtMin,
  stepperAtMax,
  addToCartLabel = '加入購物車',
}: ProductDrawerContentProps) {
  const priceCents = selectedVariant?.unitPriceTwd ?? product.productPriceTwd;
  const hasVariants = Boolean(
    product.hasVariants && product.variants && product.variants.length > 0
  );
  const variantsList = product.variants ?? [];
  const dimensionKeys = hasVariants
    ? Array.from(
        new Set(variantsList.flatMap(v => Object.keys(v.combination ?? {})))
      ).sort()
    : [];

  const allImageUrls =
    (product.imageUrls?.length ?? 0) > 0
      ? [product.imageUrl].filter(Boolean).concat(product.imageUrls ?? [])
      : product.imageUrl
        ? [product.imageUrl]
        : [];

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
        <ProductImageGallery
          primaryUrl={product.imageUrl}
          imageUrls={product.imageUrls}
          normalizedUrls={allImageUrls}
          alt={product.productName}
          thumbnail={false}
          className="mb-3"
        />
        {product.productDescription ? (
          <p className="text-sm text-gray-600 whitespace-pre-wrap">
            {product.productDescription}
          </p>
        ) : (
          <p className="text-sm text-gray-400 italic">此商品暫無描述</p>
        )}

        {/* 多規格：依維度（尺寸、顏色等）分開選擇 */}
        {hasVariants && dimensionKeys.length > 0 && (
          <div className="mt-3 space-y-3">
            {dimensionKeys.map(dimKey => {
              const values = Array.from(
                new Set(
                  variantsList
                    .map(v => v.combination?.[dimKey])
                    .filter(Boolean) as string[]
                )
              ).sort();
              return (
                <div key={dimKey}>
                  <p className="text-sm font-medium text-gray-700 mb-1.5">
                    {DIM_LABEL[dimKey] ?? dimKey}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {values.map(val => {
                      const isSelected = selectedByDimension?.[dimKey] === val;
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() =>
                            setSelectedByDimension(prev => ({
                              ...(prev ?? {}),
                              [dimKey]: val,
                            }))
                          }
                          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            isSelected
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-4 py-2 border-t flex items-center justify-between gap-2">
        <DrawerTitle className="text-base font-semibold m-0">
          {product.productName}
        </DrawerTitle>
        <span className="text-base font-semibold text-blue-600 shrink-0">
          {formatTwdFromCents(priceCents)}
        </span>
      </div>

      <DrawerFooter className="mt-0 flex-row items-center gap-4 pt-2 border-t">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onQuantityMinus}
            disabled={stepperAtMin}
            className="h-10 w-10 rounded-full border border-input bg-background shrink-0 flex items-center justify-center text-lg disabled:opacity-50"
          >
            −
          </button>
          <span className="w-10 text-center text-base font-medium tabular-nums">
            {quantity}
          </span>
          <button
            type="button"
            onClick={onQuantityPlus}
            disabled={stepperAtMax}
            className="h-10 w-10 rounded-full border border-input bg-background shrink-0 flex items-center justify-center text-lg disabled:opacity-50"
          >
            +
          </button>
        </div>
        <Button type="button" className="flex-1 min-w-0" onClick={onAddToCart}>
          {addToCartLabel}
        </Button>
      </DrawerFooter>
    </>
  );
}
